import imageCompression from 'browser-image-compression';

// Standard dimensions for cart thumbnails - preserving original preview aspect ratio
export const THUMBNAIL_DIMENSIONS = {
  width: 400,   // Width that works well for cart display
  height: 225,  // Height that maintains 16:9 ratio (400/225 = 1.78, matching preview 1600/900)
  quality: 0.9  // Higher JPEG quality (0-1)
};

/**
 * Creates a thumbnail with standard dimensions from an image
 * @param {Blob} imageBlob - The image blob to process
 * @returns {Promise<Blob>} - Blob of the standardized thumbnail
 */
const createStandardThumbnail = async (imageBlob) => {
  return new Promise((resolve, reject) => {
    // Create an image element to load the blob
    const img = new Image();
    img.onload = () => {
      // Create a canvas with the standard dimensions
      const canvas = document.createElement('canvas');
      canvas.width = THUMBNAIL_DIMENSIONS.width;
      canvas.height = THUMBNAIL_DIMENSIONS.height;
      const ctx = canvas.getContext('2d');
      
      // Fill with dark background to match the customizer
      ctx.fillStyle = '#212025';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate scaling to fit the image within the canvas while preserving aspect ratio
      const imgRatio = img.width / img.height;
      const canvasRatio = canvas.width / canvas.height;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      // Use the minimum scale factor to ensure the entire image fits
      // This preserves the aspect ratio and prevents any cropping
      if (imgRatio > canvasRatio) {
        // Image is wider than canvas ratio - fit by width
        drawWidth = canvas.width * 0.95;  // 95% of canvas width for padding
        drawHeight = drawWidth / imgRatio;
      } else {
        // Image is taller than canvas ratio - fit by height
        drawHeight = canvas.height * 0.95;  // 95% of canvas height for padding
        drawWidth = drawHeight * imgRatio;
      }
      
      // Center the image
      offsetX = (canvas.width - drawWidth) / 2;
      offsetY = (canvas.height - drawHeight) / 2;
      
      console.log(`Creating thumbnail: ${img.width}x${img.height} (${imgRatio.toFixed(3)}) -> ${drawWidth.toFixed(0)}x${drawHeight.toFixed(0)} at ${offsetX.toFixed(0)},${offsetY.toFixed(0)} on ${canvas.width}x${canvas.height} canvas`);
      
      // Draw the image centered on the canvas
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      
      // Convert the canvas to a blob
      canvas.toBlob(
        (blob) => resolve(blob),
        'image/jpeg',
        THUMBNAIL_DIMENSIONS.quality
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(imageBlob);
  });
};

/**
 * Compresses an image and returns a file URL instead of base64
 * @param {string} imageSource - The image source (base64 or blob URL)
 * @param {number} maxSizeMB - Maximum size in MB (default: 1.0)
 * @param {number} maxWidthOrHeight - Maximum width or height (default: 1600)
 * @param {boolean} standardize - Whether to standardize the dimensions (default: true)
 * @returns {Promise<string>} - URL to the compressed image file
 */
export const compressImageToFile = async (
  imageSource, 
  maxSizeMB = 1.0, 
  maxWidthOrHeight = 1600,
  standardize = true
) => {
  try {
    console.log('Compressing image source type:', 
      imageSource.startsWith('data:') ? 'data URL' : 
      imageSource.startsWith('blob:') ? 'blob URL' : 
      'other URL');
    
    // Convert source to blob
    const fetchResponse = await fetch(imageSource);
    const blob = await fetchResponse.blob();
    
    console.log('Original image size:', (blob.size / 1024).toFixed(2), 'KB');
    
    let processedBlob;
    
    if (standardize) {
      // Create a thumbnail with standard dimensions
      processedBlob = await createStandardThumbnail(blob);
      console.log('Standardized thumbnail size:', (processedBlob.size / 1024).toFixed(2), 'KB');
    } else {
      // Set compression options with higher quality settings
      const options = {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: true,
        initialQuality: 0.8, // Higher initial quality (0-1)
      };
      
      // Just compress the image without standardizing dimensions
      processedBlob = await imageCompression(blob, options);
      console.log('Compressed image size:', (processedBlob.size / 1024).toFixed(2), 'KB');
    }
    
    // Create a file from the blob
    const fileName = `compressed-${Date.now()}.jpg`;
    const compressedFile = new File([processedBlob], fileName, { 
      type: 'image/jpeg' 
    });
    
    // Create a URL for the file
    const fileUrl = URL.createObjectURL(compressedFile);
    console.log('Created file URL:', fileUrl);
    
    // Store the URL in a registry to revoke later
    if (!window.dekalioFileUrls) {
      window.dekalioFileUrls = [];
    }
    window.dekalioFileUrls.push(fileUrl);
    
    return fileUrl;
  } catch (error) {
    console.error('Error compressing image to file:', error);
    return imageSource; // Return original if compression fails
  }
};

/**
 * Revokes all file URLs created by compressImageToFile
 * Call this when the component unmounts or when the URLs are no longer needed
 */
export const revokeAllFileUrls = () => {
  if (window.dekalioFileUrls && window.dekalioFileUrls.length > 0) {
    window.dekalioFileUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Error revoking URL:', e);
      }
    });
    window.dekalioFileUrls = [];
  }
};

/**
 * Compresses an image from a base64 string
 * @param {string} base64String - The base64 image string to compress
 * @param {number} maxSizeMB - Maximum size in MB (default: 1.0)
 * @param {number} maxWidthOrHeight - Maximum width or height (default: 1600)
 * @param {boolean} standardize - Whether to standardize the dimensions (default: false)
 * @returns {Promise<string>} - Compressed base64 string
 */
export const compressBase64Image = async (
  base64String, 
  maxSizeMB = 1.0, 
  maxWidthOrHeight = 1600,
  standardize = false
) => {
  try {
    // Convert base64 to blob
    const fetchResponse = await fetch(base64String);
    const blob = await fetchResponse.blob();
    
    console.log(`Original image size: ${(blob.size / 1024).toFixed(2)} KB`);
    
    let processedBlob;
    
    if (standardize) {
      // Create a thumbnail with standard dimensions
      processedBlob = await createStandardThumbnail(blob);
    } else {
      // Set compression options with balanced quality/size
      const options = {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: true,
        initialQuality: 0.7, // Balanced quality
      };
      
      // Compress the image
      processedBlob = await imageCompression(blob, options);
      console.log(`Compressed image size: ${(processedBlob.size / 1024).toFixed(2)} KB`);
    }
    
    // Convert back to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(processedBlob);
    });
  } catch (error) {
    console.error('Error compressing image:', error);
    return base64String; // Return original if compression fails
  }
}; 